/**
 * The Business Intelligence section's menu (CU-869fmhmx8 D13, Curro's decision 6; CU-869fmjzrh): dashboards first,
 * Pricing Environment on its own, Leading Analyzer and "Advanced (BI)" last. Europcar Custom Search Analytics keeps its
 * route here (URL unchanged) but is listed under Main Page since CU-869fmherf batch 5 (`noMenu`; the Layout lists it).
 * The section header opens Advanced (BI) for a user with `bi`; without it, the first entry listed.
 *
 * Pure and alias-free, so `node --test` runs it: the routes come from `BUSINESS_LOGISTICS_ROUTES` at the call site.
 */
import { NEW_UNTIL, menuBadge, newBadge } from '../../../Layout/menu.js'

// The "NEW" list and its rule live in `Layout/menu.js`, shared by every section (CU-869fmherf batch 5)
export { NEW_UNTIL, menuBadge }

export const MENU_KEYS = Object.freeze({
	SEASON_VS_LY: 'seasonVsLastYear',
	PRICE_CURVE: 'priceCurve',
	// CU-869fmjzrh: this exact value, the Advanced BI goals route by it
	OFFICE_NETWORK: 'officeNetwork',
	EUROPCAR: 'europcar',
	PRICING_ENV: 'pricingEnvironment',
	LEADING: 'leadingAnalyzer',
	ADVANCED: 'advancedBi',
})

/**
 * The section's entries the user may see, in menu order: `[{ key, path, title, badge? }]`. Order (Curro's decision 8,
 * CU-869fmjzrg): Season vs last year · Price curve of a date · My office network · … · Advanced (BI) last. `today`
 * decides the "NEW" badges; without it none is shown.
 */
export function businessLogisticsChildren(features = {}, routes, { today = null, newUntil = NEW_UNTIL } = {}) {
	const f = features || {}
	const badge = key => newBadge(key, today, newUntil)
	return [
		...(f.bi ? [{ key: MENU_KEYS.SEASON_VS_LY, path: routes.SEASON_VS_LY_PATH, title: routes.SEASON_VS_LY_TITLE, ...badge(MENU_KEYS.SEASON_VS_LY) }] : []),
		...(f.bi ? [{ key: MENU_KEYS.PRICE_CURVE, path: routes.PRICE_CURVE_PATH, title: routes.PRICE_CURVE_TITLE, ...badge(MENU_KEYS.PRICE_CURVE) }] : []),
		// decision 8 of CU-869fmjzrg: Season · Price curve of a date · My office network · … · Advanced (BI) last
		...(f.bi ? [{ key: MENU_KEYS.OFFICE_NETWORK, path: routes.OFFICE_NETWORK_PATH, title: routes.OFFICE_NETWORK_TITLE, ...badge(MENU_KEYS.OFFICE_NETWORK) }] : []),
		// Pricing Environment is now "Week by week" of Price curve of a date (CU-869fmjzrg, CA 22): off the menu
		// Europcar: routed here, listed under Main Page after Search Analytics (CU-869fmherf batch 5, Curro's request 6)
		...(f.europcar ? [{ key: MENU_KEYS.EUROPCAR, path: routes.EUROPCAR_PATH, title: routes.EUROPCAR_MENU_TITLE, noMenu: true }] : []),
		// Leading Analyzer is retired into My office network (CU-869fmjzrh R): no entry; its address is `leadingRetiredRoute`
		...(f.bi ? [{ key: MENU_KEYS.ADVANCED, path: routes.BI_PATH, title: routes.BI_TITLE }] : []),
	]
}

/**
 * Old addresses that lead somewhere else, never listed in the menu (`noMenu`: a redirect without it draws as a blank
 * entry, DS Repricing 2026-10-20). Pricing Environment → "Week by week" of Price curve of a date, with `bi` only:
 * without it, see `pricingEnvironmentNote`. `[{ key, path, to, noMenu }]`.
 */
export function businessLogisticsRedirects(features = {}, routes) {
	const f = features || {}
	return f.bi
		? [{ key: MENU_KEYS.PRICING_ENV, path: routes.PRICING_ENV_PATH, to: `${routes.PRICE_CURVE_PATH}?view=weekly&from=pricing-environment`, noMenu: true }]
		: []
}

/** Where the section header lands: Advanced (BI) with `bi`, else the first entry listed; `null` when none is listed. */
export function sectionIndexPath(features = {}, routes) {
	if (features?.bi) return routes.BI_PATH
	return businessLogisticsChildren(features, routes).find(x => !x.noMenu)?.path || null
}

/**
 * Leading Analyzer's old address after its retirement (CU-869fmjzrh R, CA 24, P4): with `bi` it opens My office network
 * with the link translated; with only `leading_price`, a note that it moved (never a 404); otherwise no route.
 */
export function leadingRetiredRoute(features = {}) {
	if (features?.bi) return 'redirect'
	if (features?.leading_price) return 'note'
	return null
}

/**
 * The old Pricing Environment address for a user who had it (`europcar`) and has no `bi`: a note that it moved, never
 * a 404 (Curro, 2026-10-27, option B; replaces decision 9 of CU-869fmjzrg). With `bi` it redirects instead.
 */
export function pricingEnvironmentNote(features = {}) {
	return Boolean(features?.europcar && !features?.bi)
}
